/**
 * Hook for handling 3DS authentication actions
 */
import { useCallback } from 'react';
import type { Payment, PaymentOptions } from '../../../core/resources/payments';
import type { UsePaymentOptions } from '../usePaymentQuery';

interface UseThreedsAuthActionParams {
    startChallenge: any;
    startPolling: any;
    setIsLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    hookOptionsRef: React.MutableRefObject<UsePaymentOptions | undefined>;
    challengeInProgressRef: React.MutableRefObject<boolean>;
}

export function useThreedsAuthAction({
    startChallenge,
    startPolling,
    setIsLoading,
    setError,
    hookOptionsRef,
    challengeInProgressRef,
}: UseThreedsAuthActionParams) {
    const handleThreedsAuth = useCallback(
        async (payment: Payment, actionData: any, options: PaymentOptions = {}) => {
            if (actionData.metadata?.threedsSession && !challengeInProgressRef.current) {
                try {
                    challengeInProgressRef.current = true;

                    await startChallenge(
                        {
                            sessionId: actionData.metadata.threedsSession.externalSessionId,
                            acsChallengeUrl: actionData.metadata.threedsSession.acsChallengeUrl,
                            acsTransactionId: actionData.metadata.threedsSession.acsTransID,
                            threeDSVersion: actionData.metadata.threedsSession.messageVersion,
                        },
                        { provider: options.threedsProvider || 'basis_theory' },
                    );

                    challengeInProgressRef.current = false;

                    // Start polling after challenge completion
                    if (payment.id) {
                        startPolling(payment.id, {
                            onRequireAction: (updatedPayment: Payment) => {
                                // Will be handled by parent
                                options.onRequireAction?.(updatedPayment);
                            },
                            onSuccess: async (successPayment: Payment) => {
                                setIsLoading(false);
                                const response = {
                                    paymentId: successPayment.id,
                                    payment: successPayment,
                                    order: (successPayment as any).order,
                                };

                                // Hook-level callback (universal handler)
                                if (hookOptionsRef.current?.onPaymentCompleted) {
                                    await hookOptionsRef.current.onPaymentCompleted(successPayment, {
                                        isRedirectReturn: false,
                                        order: response.order,
                                    });
                                }

                                // Legacy callback (backwards compatibility)
                                options.onSuccess?.(response);
                                // Funnel-aligned callback (recommended)
                                options.onPaymentSuccess?.(response);
                            },
                            onFailure: async (errorMsg: string) => {
                                setError(errorMsg);
                                setIsLoading(false);

                                // Hook-level callback (universal handler)
                                if (hookOptionsRef.current?.onPaymentFailed) {
                                    await hookOptionsRef.current.onPaymentFailed(errorMsg, {
                                        isRedirectReturn: false,
                                    });
                                }

                                // Legacy callback (backwards compatibility)
                                options.onFailure?.(errorMsg);
                                // Funnel-aligned callback (recommended)
                                options.onPaymentFailed?.({
                                    code: 'PAYMENT_FAILED',
                                    message: errorMsg,
                                    payment,
                                });
                            },
                        });
                    }
                } catch (_error) {
                    challengeInProgressRef.current = false;
                    const errorMsg = _error instanceof Error ? _error.message : 'Failed to start 3DS challenge';
                    setError(errorMsg);
                    setIsLoading(false);
                    // Legacy callback (backwards compatibility)
                    options.onFailure?.(errorMsg);
                    // Funnel-aligned callback (recommended)
                    options.onPaymentFailed?.({
                        code: '3DS_CHALLENGE_FAILED',
                        message: errorMsg,
                        payment,
                    });
                }
            }
        },
        [startChallenge, startPolling, setIsLoading, setError, hookOptionsRef, challengeInProgressRef],
    );

    return { handleThreedsAuth };
}
